/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedData').BalancedData} BalancedData */

import { Button, Stack } from '@mui/material'
import GenericFloatingButton from '../../generic/buttons/floatingButtons/GenericFloatingButton'
import { EditRounded, HistoryOutlined, SaveRounded, SchemaOutlined } from '@mui/icons-material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { loadWorkflowIntoBalanced } from './balancedLoad'
import { useState } from 'react'
import SmartTextField from '../../generic/smarttextfield/SmartTextField'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { formatPartReference } from '../orders/partReferenceLabel'
import { formatBalancedLabel } from '../../generic/utilities/objectStructures/balanced/balancedLabel'
import { useTheme } from '@emotion/react'
import WorkflowTable from './WorkflowTable'

/**
 * Top-level page header for the balanced view.
 * Shows part reference / workflow metadata fields, a workflow selector dialog,
 * and the Edit / Save action button.
 *
 * @param {Object}        props
 * @param {Function}      props.dispatchBalancedData - Reducer dispatcher from `Balanced`
 * @param {BalancedData}  props.balancedData         - Shared balanced state from `Balanced`
 * @param {Function}      props.onRevert             - Called when user confirms revert
 */
const BalancedHeader = ({ dispatchBalancedData, balancedData, onRevert, onSave }) => {
  const { t_ } = useSmartTranslation()
  // Historia 125 (MIC-4261), delta del PO: the part reference reads in the installation's format,
  // through the same helper the request form uses. Painting and resolving with ONE format is what
  // keeps a chosen option findable (E4).
  const [frontEndConfig] = useFrontEndConfig()
  const partReferenceDisplayFormat = frontEndConfig?.partReferenceDisplayFormat
  const theme = useTheme()
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false)
  const [showRevertDialog, setShowRevertDialog] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  /**
   * Fetches the job nodes for a workflow and loads it into the shared state.
   *
   * @param {{ id: number, name: string, description: string, version: number, partReference: { id: number, code: string, name: string } }} workflow - Selected workflow row
   * @returns {Promise<void>}
   */
  const loadWorkflow = async (workflow) => {
    await loadWorkflowIntoBalanced(workflow, dispatchBalancedData)
  }

  /**
   * Loads the workflow selected in the dialog and closes it.
   *
   * @returns {Promise<void>}
   */
  const handleOnWorkflowSelected = async () => {
    if (!selectedRow) return
    await loadWorkflow(selectedRow)
    setShowWorkflowDialog(false)
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr max-content', marginTop: '1rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr 1fr 1fr 0.3fr', gap: '1rem' }}>
        <Button
          color='primary'
          variant='outlined'
          startIcon={<SchemaOutlined />}
          onClick={() => {
            setShowWorkflowDialog(true)
          }}
        >
          {t_('Select workflow')}
        </Button>
        <SmartTextField
          label={t_('Part Reference')}
          value={balancedData.partReference?.code ? formatPartReference(balancedData.partReference, partReferenceDisplayFormat) : null}
          disabled
        />
        <SmartTextField
          label={t_('Workflow name')}
          value={formatBalancedLabel(balancedData.workflow?.code, balancedData.workflow?.name)}
          disabled
        />
        <SmartTextField
          label={t_('Description')}
          value={balancedData.workflow?.description}
          disabled
        />
        <SmartTextField
          label={t_('Version')}
          value={balancedData.workflow?.version}
          disabled
          style={{ maxWidth: '5rem' }}
        />
      </div>
      <div />
      <Stack direction='row' gap={2} style={{ alignItems: 'start' }}>
        <GenericFloatingButton
          label={t_('Revert')}
          icon={<HistoryOutlined />}
          color={balancedData.editing ? theme.palette.error.light : 'disabled'}
          opacity={1}
          onClick={() => setShowRevertDialog(true)}
          disabled={!balancedData.editing}
        />
        <GenericFloatingButton
          label={balancedData.editing ? t_('Save') : t_('Edit')}
          icon={balancedData.editing ? <SaveRounded /> : <EditRounded />}
          color={theme.palette.primary.main}
          disabled={!balancedData.workflow}
          opacity={1}
          onClick={() => balancedData.editing ? setShowSaveDialog(true) : dispatchBalancedData({ type: 'SET_EDITING', editing: true })}
        />
      </Stack>
      <SmartDialog
        setOpen={showRevertDialog}
        title='default'
        message={t_('Are you sure you want to revert all changes? This action cannot be undone.')}
        acceptCallback={() => { onRevert?.(); setShowRevertDialog(false) }}
        cancelCallback={() => setShowRevertDialog(false)}
      />
      <SmartDialog
        setOpen={showSaveDialog}
        title='default'
        message={t_('Do you want to save the changes?')}
        acceptCallback={() => { setShowSaveDialog(false); onSave?.() }}
        cancelCallback={() => setShowSaveDialog(false)}
      />
      <SmartDialog
        title={createDialogTitle({ title: t_('Select a workflow'), icon: SchemaOutlined })}
        closeCallback={() => setShowWorkflowDialog(false)}
        accept
        acceptCallback={handleOnWorkflowSelected}
        cancel={false}
        close
        renderComponent={<WorkflowTable
          onWorkflowSelected={(row) => { setSelectedRow(row) }}
          partReference={balancedData.partReference}
          currentSelectedeWorkflowId={balancedData.workflow?.id}
                         />}
        setOpen={showWorkflowDialog}
      />
    </div>
  )
}

export default BalancedHeader
